import { ColorResult } from 'react-color';
import { useCallback, ChangeEvent } from 'react';

import { clampTextRotation } from '../utils/elementSettingsUtils';
import { ElementSettingsActionType } from '../types/elementSettingsTypes';
import { TextStyle, FontFamily, TextAlignment } from '../../../../../@types/forms/enums/ElementProperties';

/**
 * Custom hook for memoized element handlers
 */
export const useElementHandlers = (dispatch: any, state: any, translationFunc: (key: string) => string) => {
  // Text rotation handlers
  const handleSliderChange = useCallback(
    (_event: Event, newValue: number | number[]) => {
      dispatch({ type: ElementSettingsActionType.SET_TEXT_ROTATION, payload: newValue });
    },
    [dispatch]
  );

  const handleTextRotationInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value === '' ? '' : Number(event.target.value);
      dispatch({ type: ElementSettingsActionType.SET_TEXT_ROTATION, payload: value });
    },
    [dispatch]
  );

  const handleTextRotationBlur = useCallback(() => {
    if (state.textRotation !== null) {
      const clamped = clampTextRotation(state.textRotation);
      dispatch({ type: ElementSettingsActionType.SET_TEXT_ROTATION, payload: clamped });
    }
  }, [dispatch, state.textRotation]);

  // Color handler
  const handleTextColorChange = useCallback(
    (color: ColorResult | string) => {
      const colorValue = typeof color === 'string' ? color : color.hex;
      dispatch({ type: ElementSettingsActionType.SET_COLOR, payload: colorValue });
    },
    [dispatch]
  );

  // Element field handlers
  const handleDoclogixFieldIdChange = useCallback(
    (doclogixCoreAttributeShortName: string) => {
      dispatch({
        type: ElementSettingsActionType.UPDATE_ELEMENT_FIELD,
        payload: { doclogixCoreAttributeShortName },
      });
    },
    [dispatch]
  );

  const handleLabelChange = useCallback(
    (label: string) => {
      dispatch({ type: ElementSettingsActionType.UPDATE_ELEMENT_FIELD, payload: { label } });
    },
    [dispatch]
  );

  const handleCollaboratorChange = useCallback(
    (collaboratorId: string | undefined) => {
      dispatch({ type: ElementSettingsActionType.UPDATE_ELEMENT_FIELD, payload: { collaboratorId } });
    },
    [dispatch]
  );

  const handleFontChange = useCallback(
    (font: FontFamily) => {
      dispatch({ type: ElementSettingsActionType.UPDATE_ELEMENT_FIELD, payload: { font } });
    },
    [dispatch]
  );

  const handleFontSizeChange = useCallback(
    (fontSize: number) => {
      dispatch({ type: ElementSettingsActionType.UPDATE_ELEMENT_FIELD, payload: { fontSize } });
    },
    [dispatch]
  );

  const handleAlignmentChange = useCallback(
    (textAlignment: TextAlignment) => {
      if (textAlignment !== null) {
        dispatch({ type: ElementSettingsActionType.UPDATE_ELEMENT_FIELD, payload: { textAlignment } });
      }
    },
    [dispatch]
  );

  const handleTextStyleChange = useCallback(
    (textStyle: TextStyle) => {
      const currentStyle = state.elementTextStyle || { bold: false, italic: false };

      if (textStyle === TextStyle.Bold) {
        dispatch({
          type: ElementSettingsActionType.SET_TEXT_STYLE,
          payload: { ...currentStyle, bold: !currentStyle.bold },
        });
      } else if (textStyle === TextStyle.Italic) {
        dispatch({
          type: ElementSettingsActionType.SET_TEXT_STYLE,
          payload: { ...currentStyle, italic: !currentStyle.italic },
        });
      }
    },
    [dispatch, state.elementTextStyle]
  );

  const handleTextDecorationChange = useCallback(() => {
    const currentDecoration = state.elementTextDecoration || { underline: false };
    dispatch({
      type: ElementSettingsActionType.SET_TEXT_DECORATION,
      payload: { underline: !currentDecoration.underline },
    });
  }, [dispatch, state.elementTextDecoration]);

  // Options handlers
  const handleAddOption = useCallback(() => {
    dispatch({
      type: ElementSettingsActionType.ADD_OPTION,
      payload: translationFunc('forms.writeOptionTitle'),
    });
  }, [dispatch, translationFunc]);

  const handleDeleteOption = useCallback(
    (index: number) => {
      dispatch({ type: ElementSettingsActionType.DELETE_OPTION, payload: index });
    },
    [dispatch]
  );

  const handleOptionChange = useCallback(
    (index: number, event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      dispatch({
        type: ElementSettingsActionType.UPDATE_OPTION,
        payload: { index, value: event.target.value },
      });
    },
    [dispatch]
  );

  const handleOptionFocus = useCallback(
    (index: number, defaultValue: string, defaultOption?: string) => {
      const currentOption = state.options[index];
      if (currentOption === defaultValue || (defaultOption && currentOption === defaultOption)) {
        dispatch({
          type: ElementSettingsActionType.UPDATE_OPTION,
          payload: { index, value: '' },
        });
      }
    },
    [dispatch, state.options]
  );

  const handleOptionBlur = useCallback(
    (index: number, defaultValue: string) => {
      const currentOption = state.options[index];
      if (currentOption.trim() === '') {
        dispatch({
          type: ElementSettingsActionType.UPDATE_OPTION,
          payload: { index, value: defaultValue },
        });
      }
    },
    [dispatch, state.options]
  );

  // Form group options handlers
  const handleAddFormGroupOption = useCallback(() => {
    dispatch({
      type: ElementSettingsActionType.ADD_FORM_GROUP_OPTION,
      payload: { label: translationFunc('forms.writeOptionTitle'), isChecked: false },
    });
  }, [dispatch, translationFunc]);

  const handleDeleteFormGroupOption = useCallback(
    (index: number) => {
      dispatch({ type: ElementSettingsActionType.DELETE_FORM_GROUP_OPTION, payload: index });
    },
    [dispatch]
  );

  const handleFormGroupOptionChange = useCallback(
    (index: number, event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      dispatch({
        type: ElementSettingsActionType.UPDATE_FORM_GROUP_OPTION,
        payload: { index, value: { label: event.target.value, isChecked: false } },
      });
    },
    [dispatch]
  );

  const handleFormGroupFocus = useCallback(
    (index: number, defaultValue: string) => {
      const currentOption = state.formGroupOptions[index];
      if (currentOption.label === defaultValue) {
        dispatch({
          type: ElementSettingsActionType.UPDATE_FORM_GROUP_OPTION,
          payload: { index, value: { ...currentOption, label: '' } },
        });
      }
    },
    [dispatch, state.formGroupOptions]
  );

  const handleFormGroupBlur = useCallback(
    (index: number, defaultLabel: string) => {
      const currentOption = state.formGroupOptions[index];
      if (currentOption.label.trim() === '') {
        dispatch({
          type: ElementSettingsActionType.UPDATE_FORM_GROUP_OPTION,
          payload: { index, value: { ...currentOption, label: defaultLabel } },
        });
      }
    },
    [dispatch, state.formGroupOptions]
  );

  // Static text handler
  const handleStaticTextChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      dispatch({ type: ElementSettingsActionType.SET_STATIC_TEXT, payload: event.target.value });
    },
    [dispatch]
  );

  // User info handler
  const handleUserInfoChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      dispatch({
        type: ElementSettingsActionType.SET_USER_INFO,
        payload: parseInt(event.target.value, 10),
      });
    },
    [dispatch]
  );

  // Unique identifier handler
  const handleUniqueIdentifierChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      dispatch({ type: ElementSettingsActionType.SET_PREFIX, payload: event.target.value });
    },
    [dispatch]
  );

  // Number of characters handler
  const handleNumberOfCharactersChange = useCallback(
    (_event: Event, newValue: number | number[]) => {
      dispatch({
        type: ElementSettingsActionType.SET_NUMBER_OF_CHARACTERS,
        payload: newValue as number[],
      });
    },
    [dispatch]
  );

  return {
    handleSliderChange,
    handleTextRotationInputChange,
    handleTextRotationBlur,
    handleTextColorChange,
    handleDoclogixFieldIdChange,
    handleLabelChange,
    handleCollaboratorChange,
    handleFontChange,
    handleFontSizeChange,
    handleAlignmentChange,
    handleTextStyleChange,
    handleTextDecorationChange,
    handleAddOption,
    handleDeleteOption,
    handleOptionChange,
    handleOptionFocus,
    handleOptionBlur,
    handleAddFormGroupOption,
    handleDeleteFormGroupOption,
    handleFormGroupOptionChange,
    handleFormGroupFocus,
    handleFormGroupBlur,
    handleStaticTextChange,
    handleUserInfoChange,
    handleUniqueIdentifierChange,
    handleNumberOfCharactersChange,
  };
};
